/**
 * Booking management page (Task 15) — reschedule / cancel via manage token.
 *
 * Route (registered OUTSIDE the staff auth gate in App.tsx):
 *   /booking/:manageToken
 *
 * The manage token (from the confirmation email / success screen) is the only
 * auth. `?embed=1` strips the firm chrome for iframe use, mirroring
 * PublicBookingPage. All times render in the viewer's detected timezone.
 *
 * IMPORTANT: a successful reschedule rotates the manage token — the old link
 * is dead. We surface the new link prominently and replace the URL in-place.
 */

import { useEffect, useMemo, useState } from "react";
import { useRoute } from "wouter";
import { DateTime } from "luxon";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowLeft,
  Calendar as CalendarIcon,
  Check,
  ChevronLeft,
  ChevronRight,
  Clock,
  Copy,
  Globe,
  Loader2,
  MapPin,
  Phone,
  Video,
  XCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import {
  getManagedBooking,
  getPublicSlots,
  rescheduleBooking,
  cancelBooking,
  type ManageBooking,
  type LocationType,
  type Slot,
} from "@/lib/bookingApi";

// ---------------------------------------------------------------------------
// Constants & small helpers (mirrors PublicBookingPage's visual language)
// ---------------------------------------------------------------------------

const NAVY = "#0f2747";
const GOLD = "#d9b54a";

function detectZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "America/New_York";
  } catch {
    return "America/New_York";
  }
}

function zoneLabel(zone: string): string {
  return zone
    .replace(/_/g, " ")
    .replace("America/", "")
    .replace("Europe/", "")
    .replace("Asia/", "")
    .replace("Pacific/", "")
    .replace("Australia/", "");
}

function locationIcon(type: LocationType) {
  if (type === "zoom") return <Video className="w-4 h-4 shrink-0" />;
  if (type === "phone") return <Phone className="w-4 h-4 shrink-0" />;
  return <MapPin className="w-4 h-4 shrink-0" />;
}

function locationLabel(type: LocationType): string {
  if (type === "zoom") return "Zoom meeting";
  if (type === "phone") return "Phone call";
  return "In person";
}

// ---------------------------------------------------------------------------
// Chrome wrapper (firm header in full mode; chrome-less in embed mode)
// ---------------------------------------------------------------------------

function Chrome({ embed, children }: { embed: boolean; children: React.ReactNode }) {
  if (embed) {
    return <div className="w-full font-sans">{children}</div>;
  }
  return (
    <div className="min-h-screen bg-slate-100 font-sans">
      <header
        className="px-5 py-3 text-sm text-white flex items-center gap-2"
        style={{ backgroundColor: NAVY }}
      >
        <span className="font-semibold">Carpenter &amp; Company, CPAs</span>
        <span className="font-bold" style={{ color: GOLD }}>
          | Manage booking
        </span>
      </header>
      <main className="max-w-3xl mx-auto px-4 py-8">{children}</main>
    </div>
  );
}

function MessageCard({
  title,
  body,
  icon,
}: {
  title: string;
  body: string;
  icon?: React.ReactNode;
}) {
  return (
    <div className="bg-white border rounded-xl shadow-sm p-8 max-w-lg mx-auto text-center mt-8">
      {icon ?? <CalendarIcon className="w-10 h-10 mx-auto mb-4 text-slate-300" />}
      <h1 className="text-lg font-semibold text-slate-800 mb-2">{title}</h1>
      <p className="text-sm text-slate-500">{body}</p>
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="bg-white border rounded-xl shadow-sm p-6 max-w-lg mx-auto mt-2 space-y-3">
      <Skeleton className="h-5 w-44" />
      <Skeleton className="h-4 w-64" />
      <Skeleton className="h-4 w-52" />
      <Skeleton className="h-4 w-40" />
      <div className="flex gap-2 pt-2">
        <Skeleton className="h-9 w-28" />
        <Skeleton className="h-9 w-28" />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Month calendar grid (same UX as the public page)
// ---------------------------------------------------------------------------

interface MonthGridProps {
  year: number;
  month: number; // 1–12
  tz: string;
  availableDays: Set<string>;
  selectedDay: string | null;
  onSelectDay: (isoDate: string) => void;
  onPrevMonth: () => void;
  onNextMonth: () => void;
  canGoPrev: boolean;
  loading: boolean;
}

function MonthGrid({
  year,
  month,
  tz,
  availableDays,
  selectedDay,
  onSelectDay,
  onPrevMonth,
  onNextMonth,
  canGoPrev,
  loading,
}: MonthGridProps) {
  const monthStart = DateTime.fromObject({ year, month, day: 1 }, { zone: tz });
  const today = DateTime.now().setZone(tz).startOf("day");
  const leadingBlanks = monthStart.weekday % 7; // luxon: Mon=1..Sun=7 → Sun-first offset
  const daysInMonth = monthStart.daysInMonth ?? 30;

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <button
          type="button"
          onClick={onPrevMonth}
          disabled={!canGoPrev}
          aria-label="Previous month"
          className="p-1.5 rounded-md hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed text-slate-600"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        <div className="text-sm font-semibold text-slate-800 flex items-center gap-2">
          {monthStart.toFormat("MMMM yyyy")}
          {loading && <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400" />}
        </div>
        <button
          type="button"
          onClick={onNextMonth}
          aria-label="Next month"
          className="p-1.5 rounded-md hover:bg-slate-100 text-slate-600"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
      <div className="grid grid-cols-7 gap-1.5 text-center">
        {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => (
          <div key={i} className="text-[11px] font-medium text-slate-400 py-1">
            {d}
          </div>
        ))}
        {Array.from({ length: leadingBlanks }).map((_, i) => (
          <div key={`b${i}`} />
        ))}
        {Array.from({ length: daysInMonth }).map((_, i) => {
          const day = monthStart.plus({ days: i });
          const iso = day.toISODate()!;
          const isPast = day < today;
          const isToday = day.hasSame(today, "day");
          const available = availableDays.has(iso);
          const isSelected = selectedDay === iso;
          const disabled = isPast || !available;
          return (
            <button
              key={iso}
              type="button"
              disabled={disabled}
              onClick={() => onSelectDay(iso)}
              className={cn(
                "aspect-square rounded-lg text-xs flex items-center justify-center transition-colors",
                isSelected
                  ? "text-white font-bold"
                  : available && !isPast
                    ? "font-semibold bg-blue-50 text-[#0f2747] border border-blue-200 hover:bg-blue-100"
                    : "text-slate-300 cursor-not-allowed",
                isToday && !isSelected && "ring-1 ring-slate-400"
              )}
              style={isSelected ? { backgroundColor: NAVY } : undefined}
            >
              {day.day}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Booking details block (shared by details / past / rescheduled views)
// ---------------------------------------------------------------------------

function BookingDetails({
  booking,
  start,
  tz,
}: {
  booking: ManageBooking;
  start: string;
  tz: string;
}) {
  return (
    <div className="border rounded-lg p-4 space-y-2 text-sm text-slate-700">
      <div className="font-semibold text-slate-900">{booking.eventType.name}</div>
      <div className="text-slate-600">with {booking.staffName}</div>
      <div className="flex items-center gap-2">
        <CalendarIcon className="w-4 h-4 shrink-0" />
        {DateTime.fromISO(start).setZone(tz).toFormat("cccc, MMMM d, yyyy 'at' h:mm a")}{" "}
        <span className="text-xs text-slate-400">({zoneLabel(tz)})</span>
      </div>
      <div className="flex items-center gap-2">
        <Clock className="w-4 h-4 shrink-0" />
        {booking.eventType.durationMin} minutes
      </div>
      <div className="flex items-center gap-2">
        {locationIcon(booking.eventType.locationType)}
        {locationLabel(booking.eventType.locationType)}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

type ViewState = "details" | "reschedule" | "rescheduled" | "cancelled" | "gone";

export default function BookingManage() {
  const [, params] = useRoute("/booking/:manageToken");
  const { toast } = useToast();

  const initialToken = params?.manageToken ?? "";
  // Token rotates on reschedule — track the live one in state.
  const [token, setToken] = useState(initialToken);
  const embed = useMemo(
    () => new URLSearchParams(window.location.search).get("embed") === "1",
    []
  );

  const tz = useMemo(detectZone, []);

  // ---- booking data --------------------------------------------------------
  const bookingQuery = useQuery<ManageBooking, Error>({
    queryKey: ["managed-booking", token],
    queryFn: () => getManagedBooking(token),
    enabled: token.length > 0,
    retry: false,
  });
  const booking = bookingQuery.data ?? null;

  // ---- view state ----------------------------------------------------------
  const [view, setView] = useState<ViewState>("details");
  // In-memory copy of the booking captured at the moment a reschedule/cancel
  // succeeds. The terminal views render from this — never from the query —
  // because a reschedule rotates the token (new query key → data is undefined
  // while refetching, and the refetch may even fail) and a cancelled booking
  // may 404/410 on a later refocus refetch.
  const [bookingSnapshot, setBookingSnapshot] = useState<ManageBooking | null>(null);
  const [goneReason, setGoneReason] = useState<string | null>(null);
  const [pendingSlot, setPendingSlot] = useState<Slot | null>(null); // reschedule confirm dialog
  const [cancelOpen, setCancelOpen] = useState(false);
  const [working, setWorking] = useState(false);
  const [newStart, setNewStart] = useState<string | null>(null); // after reschedule
  const [copied, setCopied] = useState(false);

  // ---- reschedule slot picker state ---------------------------------------
  const [monthCursor, setMonthCursor] = useState<{ year: number; month: number }>(() => {
    const now = DateTime.now();
    return { year: now.year, month: now.month };
  });
  const [selectedDay, setSelectedDay] = useState<string | null>(null);

  const monthStart = DateTime.fromObject(
    { year: monthCursor.year, month: monthCursor.month, day: 1 },
    { zone: tz }
  );
  const monthEnd = monthStart.endOf("month");
  const nowUtc = DateTime.utc();
  const fromIso = (monthStart.toUTC() > nowUtc ? monthStart.toUTC() : nowUtc).toISO()!;
  const toIso = monthEnd.toUTC().toISO()!;
  const monthInPast = monthEnd.toUTC() < nowUtc;

  const slotsQuery = useQuery<{ slots: Slot[] }, Error>({
    queryKey: [
      "manage-reschedule-slots",
      booking?.pageSlug ?? "",
      booking?.eventType.slug ?? "",
      monthCursor.year,
      monthCursor.month,
    ],
    queryFn: () =>
      getPublicSlots(booking!.pageSlug, booking!.eventType.slug, fromIso, toIso),
    enabled: view === "reschedule" && !!booking && !monthInPast,
    retry: false,
    staleTime: 30_000,
  });

  const slotsByDay = useMemo(() => {
    const map = new Map<string, Slot[]>();
    for (const slot of slotsQuery.data?.slots ?? []) {
      const key = DateTime.fromISO(slot.start).setZone(tz).toISODate();
      if (!key) continue;
      const list = map.get(key) ?? [];
      list.push(slot);
      map.set(key, list);
    }
    for (const list of map.values()) list.sort((a, b) => a.start.localeCompare(b.start));
    return map;
  }, [slotsQuery.data, tz]);

  const availableDays = useMemo(() => new Set(slotsByDay.keys()), [slotsByDay]);

  // Default the selected day to the first available day once slots load.
  useEffect(() => {
    if (view !== "reschedule") return;
    if (selectedDay && availableDays.has(selectedDay)) return;
    const first = Array.from(availableDays).sort()[0] ?? null;
    setSelectedDay(first);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableDays, view]);

  // ---- actions -------------------------------------------------------------
  const newManageUrl = `${window.location.origin}/booking/${token}${embed ? "?embed=1" : ""}`;

  const copyNewLink = async () => {
    try {
      await navigator.clipboard.writeText(newManageUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      toast({ title: "Copy failed", description: newManageUrl });
    }
  };

  const doReschedule = async () => {
    if (!pendingSlot || working) return;
    setWorking(true);
    try {
      const result = await rescheduleBooking(token, pendingSlot.start, pendingSlot.end);
      if (!result.ok) {
        if (result.reason === "taken") {
          toast({
            title: "That time was just taken",
            description: "Someone else grabbed that slot. Please pick another time.",
            variant: "destructive",
          });
          setPendingSlot(null);
          await slotsQuery.refetch();
          return;
        }
        // 410 — booking is no longer reschedulable.
        setGoneReason(result.reason);
        setPendingSlot(null);
        setView("gone");
        return;
      }
      // Success — the old token is dead. Snapshot the booking BEFORE swapping
      // the token: the new query key has no cached data, so `booking` goes
      // null while the refetch runs (and the refetch may fail).
      if (booking) setBookingSnapshot(booking);
      setNewStart(pendingSlot.start);
      setPendingSlot(null);
      setToken(result.manageToken);
      window.history.replaceState(
        null,
        "",
        `/booking/${result.manageToken}${embed ? "?embed=1" : ""}`
      );
      setView("rescheduled");
    } catch (err) {
      toast({
        title: "Couldn't reschedule",
        description: err instanceof Error ? err.message : "Please try again.",
        variant: "destructive",
      });
      setPendingSlot(null);
    } finally {
      setWorking(false);
    }
  };

  const doCancel = async () => {
    if (working) return;
    setWorking(true);
    try {
      await cancelBooking(token);
      if (booking) setBookingSnapshot(booking);
      setCancelOpen(false);
      setView("cancelled");
    } catch (err) {
      toast({
        title: "Couldn't cancel",
        description: err instanceof Error ? err.message : "Please try again.",
        variant: "destructive",
      });
    } finally {
      setWorking(false);
    }
  };

  // ---- render: load / error states ----------------------------------------
  if (!initialToken) {
    return (
      <Chrome embed={embed}>
        <MessageCard
          title="This booking link is invalid or no longer active."
          body="Double-check the link from your confirmation email, or contact the firm directly."
        />
      </Chrome>
    );
  }

  // ---- render: terminal local states ---------------------------------------
  // These MUST come before the query loading/error returns: after a reschedule
  // the token swap re-keys bookingQuery (loading, possibly erroring), and these
  // views depend only on in-memory state (snapshot + newStart + new token).
  const localBooking = bookingSnapshot ?? booking;

  if (view === "cancelled" && localBooking) {
    return (
      <Chrome embed={embed}>
        <MessageCard
          icon={<XCircle className="w-10 h-10 mx-auto mb-4 text-rose-400" />}
          title="Your meeting has been cancelled"
          body={`${localBooking.eventType.name} with ${localBooking.staffName} on ${DateTime.fromISO(
            localBooking.start
          )
            .setZone(tz)
            .toFormat("cccc, MMMM d 'at' h:mm a")} has been cancelled. A confirmation email is on its way.`}
        />
      </Chrome>
    );
  }

  if (view === "gone") {
    return (
      <Chrome embed={embed}>
        <MessageCard
          title={
            goneReason === "invalid_range"
              ? "That time can't be booked"
              : "This booking can no longer be changed"
          }
          body={
            goneReason === "invalid_range"
              ? "The selected time isn't valid for this meeting type. Please reload the page and pick another time."
              : "This booking was cancelled or is no longer active. If you still need a meeting, please book a new time."
          }
        />
      </Chrome>
    );
  }

  if (view === "rescheduled" && newStart && localBooking) {
    return (
      <Chrome embed={embed}>
        <div className="bg-white border rounded-xl shadow-sm p-6 max-w-lg mx-auto mt-2">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
              <Check className="w-4 h-4 text-emerald-600" />
            </div>
            <h1 className="text-base font-semibold text-slate-900">Meeting rescheduled</h1>
          </div>
          <BookingDetails booking={localBooking} start={newStart} tz={tz} />
          <p className="text-xs text-slate-500 mt-3">
            An updated confirmation email is on its way to{" "}
            <span className="font-medium">{localBooking.inviteeEmail}</span>.
          </p>
          <div className="mt-4">
            <Label className="text-xs text-slate-500">
              Your old link no longer works — use this new link to reschedule or cancel:
            </Label>
            <div className="flex items-center gap-2 mt-1.5">
              <Input readOnly value={newManageUrl} className="h-8 text-xs" />
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 shrink-0"
                onClick={copyNewLink}
              >
                {copied ? (
                  <Check className="w-3.5 h-3.5 text-emerald-600" />
                ) : (
                  <Copy className="w-3.5 h-3.5" />
                )}
                <span className="ml-1.5">{copied ? "Copied" : "Copy"}</span>
              </Button>
            </div>
          </div>
        </div>
      </Chrome>
    );
  }

  if (bookingQuery.isLoading) {
    return (
      <Chrome embed={embed}>
        <LoadingSkeleton />
      </Chrome>
    );
  }

  // 404 (bad/rotated token) and 500 (e.g. tables not migrated yet) both land
  // here — show the same friendly card rather than a raw error.
  if (bookingQuery.isError || !booking) {
    return (
      <Chrome embed={embed}>
        <MessageCard
          title="This booking link is invalid or no longer active."
          body="Double-check the link from your confirmation email, or contact the firm directly. If you rescheduled, use the newest link you were sent."
        />
      </Chrome>
    );
  }

  // ---- render: server-side terminal states ---------------------------------
  if (booking.status === "cancelled") {
    return (
      <Chrome embed={embed}>
        <MessageCard
          icon={<XCircle className="w-10 h-10 mx-auto mb-4 text-rose-400" />}
          title="This booking was cancelled."
          body="If you still need a meeting, please book a new time."
        />
      </Chrome>
    );
  }

  const isPast = DateTime.fromISO(booking.end) < DateTime.utc();
  if (isPast) {
    return (
      <Chrome embed={embed}>
        <div className="bg-white border rounded-xl shadow-sm p-6 max-w-lg mx-auto mt-2">
          <h1 className="text-base font-semibold text-slate-900 mb-4">Your booking</h1>
          <BookingDetails booking={booking} start={booking.start} tz={tz} />
          <p className="text-sm text-slate-500 mt-4">This meeting has already happened.</p>
        </div>
      </Chrome>
    );
  }

  // ---- render: reschedule slot picker --------------------------------------
  if (view === "reschedule") {
    const daySlots = selectedDay ? (slotsByDay.get(selectedDay) ?? []) : [];
    const nowMonth = DateTime.now().setZone(tz);
    const canGoPrev =
      monthCursor.year > nowMonth.year ||
      (monthCursor.year === nowMonth.year && monthCursor.month > nowMonth.month);

    return (
      <Chrome embed={embed}>
        <div className={cn("bg-white border rounded-xl shadow-sm p-6", embed ? "" : "mt-2")}>
          <button
            type="button"
            onClick={() => setView("details")}
            className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800 mb-4"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Back to booking details
          </button>
          <h1 className="text-base font-semibold text-slate-900">
            Pick a new time for {booking.eventType.name}
          </h1>
          <p className="text-xs text-slate-500 mt-1 mb-5 flex items-center gap-1.5">
            <Globe className="w-3.5 h-3.5" />
            Times shown in {zoneLabel(tz)} · currently{" "}
            {DateTime.fromISO(booking.start).setZone(tz).toFormat("ccc, MMM d 'at' h:mm a")}
          </p>

          <div className="flex flex-col md:flex-row gap-6">
            <div className="flex-1 min-w-0">
              <MonthGrid
                year={monthCursor.year}
                month={monthCursor.month}
                tz={tz}
                availableDays={availableDays}
                selectedDay={selectedDay}
                onSelectDay={setSelectedDay}
                onPrevMonth={() =>
                  setMonthCursor((c) =>
                    c.month === 1
                      ? { year: c.year - 1, month: 12 }
                      : { year: c.year, month: c.month - 1 }
                  )
                }
                onNextMonth={() =>
                  setMonthCursor((c) =>
                    c.month === 12
                      ? { year: c.year + 1, month: 1 }
                      : { year: c.year, month: c.month + 1 }
                  )
                }
                canGoPrev={canGoPrev}
                loading={slotsQuery.isFetching}
              />
              {slotsQuery.isError && (
                <p className="text-xs text-rose-600 mt-3">
                  Couldn't load available times — please try again shortly.
                </p>
              )}
              {!slotsQuery.isFetching && !slotsQuery.isError && availableDays.size === 0 && (
                <p className="text-xs text-slate-500 mt-3">
                  No times available this month — try the next month.
                </p>
              )}
            </div>
            <div className="md:w-40 shrink-0">
              {selectedDay && (
                <>
                  <div className="text-xs font-semibold text-slate-700 mb-2">
                    {DateTime.fromISO(selectedDay, { zone: tz }).toFormat("ccc, MMM d")}
                  </div>
                  <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                    {daySlots.map((slot) => (
                      <button
                        key={slot.start}
                        type="button"
                        disabled={working}
                        onClick={() => setPendingSlot(slot)}
                        className="w-full border border-blue-200 rounded-lg py-2 text-xs font-semibold text-[#0f2747] bg-white hover:bg-blue-50 disabled:opacity-50 flex items-center justify-center gap-1.5"
                      >
                        {DateTime.fromISO(slot.start).setZone(tz).toFormat("h:mm a")}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Reschedule confirm dialog */}
        <AlertDialog
          open={!!pendingSlot}
          onOpenChange={(open) => {
            if (!open && !working) setPendingSlot(null);
          }}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Move this meeting?</AlertDialogTitle>
              <AlertDialogDescription>
                {pendingSlot && (
                  <>
                    Move {booking.eventType.name} with {booking.staffName} to{" "}
                    <span className="font-semibold text-slate-800">
                      {DateTime.fromISO(pendingSlot.start)
                        .setZone(tz)
                        .toFormat("cccc, MMMM d 'at' h:mm a")}
                    </span>{" "}
                    ({zoneLabel(tz)})?
                  </>
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={working}>Keep current time</AlertDialogCancel>
              <AlertDialogAction
                disabled={working}
                onClick={(e) => {
                  e.preventDefault();
                  void doReschedule();
                }}
                className="text-white"
                style={{ backgroundColor: NAVY }}
              >
                {working && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Confirm new time
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </Chrome>
    );
  }

  // ---- render: details + actions (confirmed, future) -----------------------
  return (
    <Chrome embed={embed}>
      <div className={cn("bg-white border rounded-xl shadow-sm p-6 max-w-lg mx-auto", embed ? "" : "mt-2")}>
        <h1 className="text-base font-semibold text-slate-900 mb-1">Your booking</h1>
        <p className="text-xs text-slate-500 mb-4">
          Booked for {booking.inviteeName} ({booking.inviteeEmail})
        </p>
        <BookingDetails booking={booking} start={booking.start} tz={tz} />
        <div className="flex items-center gap-2 mt-5">
          <Button
            type="button"
            className="text-white"
            style={{ backgroundColor: NAVY }}
            onClick={() => setView("reschedule")}
          >
            Reschedule
          </Button>
          <Button
            type="button"
            variant="outline"
            className="text-rose-600 border-rose-200 hover:bg-rose-50 hover:text-rose-700"
            onClick={() => setCancelOpen(true)}
          >
            Cancel meeting
          </Button>
        </div>
      </div>

      {/* Cancel confirm dialog */}
      <AlertDialog open={cancelOpen} onOpenChange={(open) => !working && setCancelOpen(open)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel this meeting?</AlertDialogTitle>
            <AlertDialogDescription>
              {booking.eventType.name} with {booking.staffName} on{" "}
              <span className="font-semibold text-slate-800">
                {DateTime.fromISO(booking.start)
                  .setZone(tz)
                  .toFormat("cccc, MMMM d 'at' h:mm a")}
              </span>{" "}
              ({zoneLabel(tz)}) will be cancelled. This can't be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={working}>Keep meeting</AlertDialogCancel>
            <AlertDialogAction
              disabled={working}
              onClick={(e) => {
                e.preventDefault();
                void doCancel();
              }}
              className="bg-rose-600 hover:bg-rose-700 text-white"
            >
              {working && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Cancel meeting
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Chrome>
  );
}
